// =============================================================================
// demoData.ts — Mock data for ORA Outlook Add-in demo / stub mode.
//
// Used by both the Layout-header demo toggle (kept from v1) and the
// stubApi in services/orchestratorApi.ts. Shapes mirror substrate test
// fixtures so the UI under stub mode looks identical to a live run:
//   - DEMO_ACTIVE_WORKFLOWS:  test_list_my_workflow_instances.py:88–101
//   - DEMO_METRICS:           ad-hoc — substrate has no metrics endpoint yet
//   - DEMO_CATALOG:           test_carrier_mortgagee_update.py:89–100 +
//                             test_analyze_email.py:77–88
//   - DEMO_HITL_QUESTION:     test_carrier_mortgagee_update.py:96–101
// =============================================================================

import type {
  ActiveWorkflow,
  EmailRunStateResponse,
  HitlQuestion,
  InstanceStatus,
  WaitingForMeResponse,
  WorkflowCatalogEntry,
} from "../types/workflow";
import type { ClientDetails, PolicyInForce } from "../types/client";

const _now = (offsetMs = 0): string =>
  new Date(Date.now() + offsetMs).toISOString();

// -----------------------------------------------------------------------------
// Active workflow rows — substrate `list_my_workflow_instances` shape.
// -----------------------------------------------------------------------------

export const DEMO_ACTIVE_WORKFLOWS: ActiveWorkflow[] = [
  {
    instance_id: "demo-instance-001",
    workflow_id: "carrier_mortgagee_update",
    name: "Johnson, Margaret — POL-2025-447821",
    region: "Northeast",
    branch: "Highstreet HQ",
    status: "running",
    current_phase: "draft",
    major_version: 1,
    created_at: _now(-3 * 60_000),
    updated_at: _now(-30_000),
  },
  {
    instance_id: "demo-instance-002",
    workflow_id: "carrier_mortgagee_update",
    name: "Chen, David — TRV-118-2200913",
    region: "West",
    branch: "Highstreet West",
    status: "paused_hitl",
    current_phase: "approve",
    major_version: 1,
    created_at: _now(-28 * 60_000),
    updated_at: _now(-5 * 60_000),
  },
];

export const DEMO_METRICS = { total: 127, hours_saved: 42 };

// -----------------------------------------------------------------------------
// Catalog — substrate `get_workflow_catalog` shape. The two showcase
// workflows that actually exist in the substrate today: carrier_mortgagee_update
// (HITL endorsement) and analyze_email (async two-phase parse).
// -----------------------------------------------------------------------------

export const DEMO_CATALOG: WorkflowCatalogEntry[] = [
  {
    ref: "carrier_mortgagee_update",
    process: "endorsement",
    id: "carrier_mortgagee_update",
    name: "Mortgagee Change",
    description:
      "Update the mortgagee information on the carrier portal for the given policy. Surfaces a Human-In-The-Loop approval before submission.",
    params: [
      { name: "policy_number", required: true, description: "Policy number" },
      { name: "carrier_id", required: true, description: "Carrier portal id e.g. travelers" },
      { name: "mortgagee_name", required: true, description: "Mortgagee / lender name" },
      { name: "mortgagee_clause", required: false, description: "Loan number or ISAOA clause" },
    ],
    phase_count: 5,
    triggers: ["adhoc", "email"],
    estimated_manual_minutes: 15,
    hidden: false,
    summary_fields: ["policy_number", "changes", "carrier_reference_id"],
    input_schema: {
      type: "object",
      required: ["policy_number", "carrier_id", "mortgagee_name"],
      properties: {
        policy_number: { type: "string", description: "Policy number" },
        carrier_id: {
          type: "string",
          description: "Carrier portal id",
          enum: ["travelers", "progressive", "nationwide"],
        },
        mortgagee_name: { type: "string" },
        mortgagee_clause: { type: "string" },
      },
    },
  },
  {
    ref: "analyze_email",
    process: "ai",
    id: "analyze_email",
    name: "Analyze Email",
    description:
      "Two-step async analysis of an inbound email — extracts client, policy, and recommended workflow.",
    params: [
      { name: "email_subject", required: true, description: "Email subject" },
      { name: "email_body", required: true, description: "Email body text" },
      { name: "email_sender", required: true, description: "From: address" },
    ],
    phase_count: 2,
    triggers: ["email", "adhoc"],
    estimated_manual_minutes: 5,
    hidden: false,
    email_prefill: true,
    summary_fields: ["client_name", "policy_number", "recommended_workflow_id"],
    input_schema: {
      type: "object",
      required: ["email_subject", "email_body", "email_sender"],
      properties: {
        email_subject: { type: "string" },
        email_body: { type: "string" },
        email_sender: { type: "string" },
      },
    },
  },
  {
    ref: "policy_lookup",
    process: "servicing",
    id: "policy_lookup",
    name: "Policy Lookup",
    description:
      "Log into Epic, search for the client and policy, and return a structured policy data summary.",
    params: [
      { name: "client_name", required: true, description: "Last name, or Last, First format" },
      { name: "policy_number", required: false, description: "Policy number hint (optional)" },
      { name: "line_of_business", required: false, description: "e.g. Homeowners, Commercial Property" },
    ],
    phase_count: 7,
    triggers: ["adhoc"],
    estimated_manual_minutes: 5,
    hidden: false,
  },
];

// -----------------------------------------------------------------------------
// HITL question — exactly mirrors the carrier_mortgagee_update shape.
// -----------------------------------------------------------------------------

export const DEMO_HITL_QUESTION: HitlQuestion = {
  question:
    "Approve this mortgagee update before we submit it to the Travelers portal?",
  choices: ["approve", "reject"],
  policy_number: "POL-2025-447821",
  changes: [
    {
      action: "add",
      mortgagee: {
        name: "First Bank",
        loan_number: "FB-12345",
        address: "100 Main St, Boston MA 02110",
      },
    },
    {
      action: "remove",
      mortgagee: {
        name: "Old Bank",
        loan_number: "LN-999",
        address: "Somewhere",
      },
    },
  ],
};

// -----------------------------------------------------------------------------
// Per-instance status snapshots used by stubApi.getStatus / pollAnalysis.
// -----------------------------------------------------------------------------

export const DEMO_INSTANCE_STATUSES: Record<string, InstanceStatus> = {
  "demo-instance-001": {
    instance_id: "demo-instance-001",
    workflow_id: "carrier_mortgagee_update",
    status: "running",
    current_phase: "draft",
    created_at: _now(-3 * 60_000),
    updated_at: _now(-30_000),
  },
  "demo-instance-002": {
    instance_id: "demo-instance-002",
    workflow_id: "carrier_mortgagee_update",
    status: "paused_hitl",
    current_phase: "approve",
    created_at: _now(-28 * 60_000),
    updated_at: _now(-5 * 60_000),
    hitl_question: DEMO_HITL_QUESTION,
  },
};

// -----------------------------------------------------------------------------
// Client details — used by the SelectedClientTile + KPI tiles.
// -----------------------------------------------------------------------------

const DEMO_POLICIES: PolicyInForce[] = [
  {
    policy_line_sk: 9001,
    policy_type: "Homeowners",
    expiration_date: "2025-09-30",
    expiring_premium: 2150,
    renewal_likelihood: "High",
    status: "active",
    policy_number: "HO-9001",
    effective_date: "2024-09-30",
    product_group: "Homeowners",
    carrier_id: "CARR-100",
    carrier_name: "Demo Mutual",
  },
  {
    policy_line_sk: 9002,
    policy_type: "Personal Auto",
    expiration_date: "2025-11-12",
    expiring_premium: 1820,
    renewal_likelihood: "Medium",
    status: "active",
    policy_number: "PA-9002",
    effective_date: "2024-11-12",
    product_group: "Personal Auto",
    carrier_id: "CARR-200",
    carrier_name: "Demo Auto Casualty",
  },
  {
    policy_line_sk: 9003,
    policy_type: "Personal Umbrella",
    expiration_date: "2025-09-30",
    expiring_premium: 410,
    renewal_likelihood: "High",
    status: "active",
    policy_number: "PU-9003",
    effective_date: "2024-09-30",
    product_group: "Personal Umbrella",
    carrier_id: "CARR-100",
    carrier_name: "Demo Mutual",
  },
];

export const DEMO_CLIENT_DETAILS: ClientDetails = {
  client: {
    client_sk: 4810024,
    client_id: "EPIC-883301",
    client_name: "Johnson, Margaret",
    email: "mjohnson@example.com",
    city: "Boston",
    state: "MA",
    branch_name: "Highstreet HQ",
    client_type: "Personal",
  },
  policies_in_force: DEMO_POLICIES,
  opportunities: [
    {
      opportunityId: "opp-001",
      policyType: "Personal Cyber",
      reasoning: "Existing high-value HO + auto book; no cyber coverage in force.",
      estimatedPremium: 320,
    },
  ],
  metrics: {
    total_completed_workflows: 127,
    hours_saved: 42,
  },
};

export const DEMO_POLICIES_BY_CLIENT_SK: Record<number, PolicyInForce[]> = {
  4810024: DEMO_POLICIES,
};

// -----------------------------------------------------------------------------
// Logs + push channel — used by stubApi.getInstanceLogs / getPushChannel.
// -----------------------------------------------------------------------------

export const DEMO_INSTANCE_LOGS: Record<
  string,
  { timestamp: string; level: "debug" | "info" | "warn" | "error"; phase_id?: string; message: string }[]
> = {
  "demo-instance-001": [
    { timestamp: _now(-3 * 60_000), level: "info", phase_id: "lookup", message: "Step started." },
    { timestamp: _now(-2 * 60_000), level: "info", phase_id: "lookup", message: "Resolved client EPIC-883301." },
    { timestamp: _now(-90_000), level: "info", phase_id: "draft", message: "Step started." },
    { timestamp: _now(-30_000), level: "debug", phase_id: "draft", message: "Built 2 mortgagee changes." },
  ],
};

export const DEMO_PUSH_CHANNEL = {
  url: "wss://demo-signalr.local/instances/demo",
  access_token: "demo-push-token",
  group: "wf-demo",
  expires_at: _now(60 * 60_000),
};

// -----------------------------------------------------------------------------
// Pinned pane (story pinned-pane) — stub fixtures.
//
// The add-in DEFAULTS to stub mode (VITE_USE_STUB_API=1), so these are what a
// browser-opened pane renders: the queue must be exercisable, and the context
// strip must reach EVERY one of its six states, without a live stack. That is
// also what makes the Phase 6 render probes runnable without Outlook.
//
// DETERMINISTIC by construction: `waiting_since` is offset from a FIXED epoch,
// not from Date.now(), so two renders of the same fixture produce the same
// elapsed text and a probe's two runs agree. The email-state selector is a
// pure function of the identity string.
// -----------------------------------------------------------------------------

/** Fixed reference instant — 2026-08-27T12:00:00Z. Never `Date.now()`. */
const DEMO_EPOCH_MS = Date.UTC(2026, 7, 27, 12, 0, 0);
const _demoIso = (offsetMs = 0): string =>
  new Date(DEMO_EPOCH_MS + offsetMs).toISOString();

export const DEMO_USER_PUSH_CHANNEL = {
  url: "wss://demo-signalr.local/users/demo",
  access_token: "demo-user-push-token",
  group: "user-demo-oid",
  expires_at: _now(60 * 60_000),
};

/**
 * Queue rows in the SERVER's order — longest wait first, `waiting_since: null`
 * (unknown) sorting last. Two `needs_you` rows and two not, so both groups
 * render and the "listed but not notified" case is visible in stub mode.
 */
export const DEMO_WAITING_QUEUE: WaitingForMeResponse = {
  items: [
    {
      instance_id: "demo-waiting-0001",
      workflow_id: "carrier_mortgagee_update_v2",
      item_type: "run",
      status: "paused_hitl",
      waiting_since: _demoIso(-3 * 60 * 60_000),
      waiting_for: "Confirm the new mortgagee clause before we send it.",
      checkpoint_id: "confirm_mortgagee",
      current_step_id: "carrier_confirm",
      // Story 28633: the queue row's progress bar ("Stage 2 of 4").
      stage_index: 2,
      stage_total: 4,
      milestones_done: 5,
      milestone_total: 11,
      client_name: "Sarah Mitchell",
      policy_number: "TRV-HO-2024-88412",
      updated_at: _demoIso(-3 * 60 * 60_000),
      needs_you: true,
    },
    {
      instance_id: "demo-waiting-0002",
      workflow_id: "carrier_mortgagee_update_v2",
      item_type: "run",
      status: "paused_hitl",
      waiting_since: _demoIso(-45 * 60_000),
      waiting_for: "The carrier portal returned two matching policies.",
      checkpoint_id: "disambiguate_policy",
      current_step_id: "carrier_lookup",
      stage_index: 1,
      stage_total: 4,
      milestones_done: 1,
      milestone_total: 11,
      client_name: "Northwood Holdings",
      policy_number: "CNA-CP-2025-11902",
      updated_at: _demoIso(-45 * 60_000),
      needs_you: true,
    },
    {
      instance_id: "demo-waiting-0003",
      workflow_id: "carrier_mortgagee_update_v2",
      item_type: "run",
      status: "warm_hitl",
      waiting_since: _demoIso(-90_000),
      waiting_for: "Review the drafted endorsement request.",
      checkpoint_id: "review_draft",
      current_step_id: "draft",
      stage_index: 2,
      stage_total: 4,
      milestones_done: 6,
      milestone_total: 11,
      client_name: "Delta Freight Co",
      policy_number: "HTF-CA-2025-40118",
      updated_at: _demoIso(-90_000),
      needs_you: false,
    },
    {
      instance_id: "demo-waiting-0004",
      workflow_id: "mortgagee_update_v1_2",
      item_type: "run",
      status: "paused_hitl",
      // UNKNOWN — parked before the waiting_since column landed. Renders
      // "unknown", never zero and never "just now" (PRD ad-2).
      waiting_since: null,
      waiting_for: "Confirm the client before we continue.",
      checkpoint_id: "confirm_client",
      current_step_id: "client_confirm",
      client_name: "Harbor Point LLC",
      policy_number: null,
      updated_at: _demoIso(-6 * 60 * 60_000),
      needs_you: false,
    },
  ],
  // The SERVER's counts. Deliberately LARGER than items.length so a stub-mode
  // pane demonstrates that the badge is the server's number and not a length.
  count: 6,
  needs_you_count: 2,
  running_count: 3,
  triaged_today: 27,
  as_of: _demoIso(0),
};

/**
 * Six canned email-state responses, one per server state. The stub picks one
 * from a stable hash of the identity so the SAME email always resolves to the
 * SAME state across renders and reloads, while clicking through a handful of
 * messages walks every state.
 */
export const DEMO_EMAIL_RUN_STATES: Record<string, EmailRunStateResponse> = {
  not_analyzed: {
    state: "not_analyzed",
    active_count: 0,
    needs_you_count: 0,
    completed_at: null,
    runs: [],
    brief: null,
    primary_run_id: null,
    has_any_run: false,
  },
  brief_ready: {
    state: "brief_ready",
    active_count: 0,
    needs_you_count: 0,
    completed_at: null,
    runs: [],
    brief: {
      client_name: "Sarah Mitchell",
      policy_number: "TRV-HO-2024-88412",
      recommended_workflow_id: "carrier_mortgagee_update_v2",
      mlflow_trace_id: "demo-trace-0001",
    },
    primary_run_id: null,
    has_any_run: false,
  },
  running: {
    state: "running",
    active_count: 1,
    needs_you_count: 0,
    completed_at: null,
    runs: [
      {
        instance_id: "demo-run-running",
        workflow_id: "carrier_mortgagee_update_v2",
        status: "running",
        is_active: true,
        waiting_since: null,
        waiting_for_person: false,
        needs_you: false,
        current_step_id: "draft",
        policy_number: "TRV-HO-2024-88412",
        created_at: _demoIso(-12 * 60_000),
        updated_at: _demoIso(-60_000),
      },
    ],
    brief: null,
    primary_run_id: "demo-run-running",
    has_any_run: true,
  },
  input_required: {
    state: "input_required",
    active_count: 1,
    needs_you_count: 1,
    completed_at: null,
    runs: [
      {
        instance_id: "demo-waiting-0001",
        workflow_id: "carrier_mortgagee_update_v2",
        status: "paused_hitl",
        is_active: true,
        waiting_since: _demoIso(-3 * 60 * 60_000),
        waiting_for_person: true,
        needs_you: true,
        current_step_id: "carrier_confirm",
        policy_number: "TRV-HO-2024-88412",
        created_at: _demoIso(-4 * 60 * 60_000),
        updated_at: _demoIso(-3 * 60 * 60_000),
      },
    ],
    brief: null,
    primary_run_id: "demo-waiting-0001",
    has_any_run: true,
  },
  completed: {
    state: "completed",
    active_count: 0,
    needs_you_count: 0,
    completed_at: _demoIso(-26 * 60 * 60_000),
    runs: [
      {
        instance_id: "demo-run-completed",
        workflow_id: "carrier_mortgagee_update_v2",
        status: "completed",
        is_active: false,
        waiting_since: null,
        waiting_for_person: false,
        needs_you: false,
        current_step_id: "closing",
        policy_number: "TRV-HO-2024-88412",
        created_at: _demoIso(-28 * 60 * 60_000),
        updated_at: _demoIso(-26 * 60 * 60_000),
      },
    ],
    brief: null,
    primary_run_id: "demo-run-completed",
    has_any_run: true,
  },
  several_runs: {
    state: "several_runs",
    active_count: 2,
    needs_you_count: 1,
    completed_at: null,
    runs: [
      {
        instance_id: "demo-waiting-0002",
        workflow_id: "carrier_mortgagee_update_v2",
        status: "paused_hitl",
        is_active: true,
        waiting_since: _demoIso(-45 * 60_000),
        waiting_for_person: true,
        needs_you: true,
        current_step_id: "carrier_lookup",
        policy_number: "CNA-CP-2025-11902",
        created_at: _demoIso(-70 * 60_000),
        updated_at: _demoIso(-45 * 60_000),
      },
      {
        instance_id: "demo-run-second",
        workflow_id: "carrier_mortgagee_update_v2",
        status: "running",
        is_active: true,
        waiting_since: null,
        waiting_for_person: false,
        needs_you: false,
        current_step_id: "draft",
        policy_number: "CNA-CP-2025-40771",
        created_at: _demoIso(-20 * 60_000),
        updated_at: _demoIso(-2 * 60_000),
      },
    ],
    brief: null,
    primary_run_id: "demo-waiting-0002",
    has_any_run: true,
  },
};

const _DEMO_EMAIL_STATE_ORDER = [
  "brief_ready",
  "input_required",
  "running",
  "completed",
  "several_runs",
  "not_analyzed",
] as const;

/**
 * Stable, order-independent pick: the SAME identity always yields the SAME
 * state. FNV-1a over the identity so a probe that captures one email's stub
 * response can assert it again on a later run.
 */
export function demoEmailRunStateFor(
  messageIdentity: string,
): EmailRunStateResponse {
  let h = 0x811c9dc5;
  for (let i = 0; i < messageIdentity.length; i++) {
    h ^= messageIdentity.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  const key = _DEMO_EMAIL_STATE_ORDER[h % _DEMO_EMAIL_STATE_ORDER.length];
  return DEMO_EMAIL_RUN_STATES[key];
}
